import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, Settings, Cloud,
  LayoutDashboard, Bell, Sliders, ScrollText,
  Server, Database, Zap, Package, Archive, Shuffle,
  Globe, MessageSquare, UserCheck, Lock, Network,
  Link2, Cpu, Radio, HardDrive, Layers,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';


const AWS_SERVICES = [
  { id: 'dashboard',   label: 'Dashboard NOC',  namespace: null,                      icon: LayoutDashboard },
  { id: 'alarms',      label: 'Alarmas',         namespace: null,                      icon: Bell },
  { id: 'alerts',      label: 'Mis Alertas',     namespace: null,                      icon: Sliders },
  { id: 'logs',        label: 'Logs',            namespace: null,                      icon: ScrollText },
  { id: 'ec2',         label: 'EC2',             namespace: 'AWS/EC2',                 icon: Server },
  { id: 'rds',         label: 'RDS',             namespace: 'AWS/RDS',                 icon: Database },
  { id: 'lambda',      label: 'Lambda',          namespace: 'AWS/Lambda',              icon: Zap },
  { id: 'ecs',         label: 'ECS',             namespace: 'AWS/ECS',                 icon: Package },
  { id: 's3',          label: 'S3',              namespace: 'AWS/S3',                  icon: Archive },
  { id: 'elb',         label: 'ELB',             namespace: 'AWS/ApplicationELB',      icon: Shuffle },
  { id: 'apigw',       label: 'API Gateway',     namespace: 'AWS/ApiGateway',          icon: Globe },
  { id: 'dynamodb',    label: 'DynamoDB',        namespace: 'AWS/DynamoDB',            icon: HardDrive },
  { id: 'sqs',         label: 'SQS',             namespace: 'AWS/SQS',                 icon: MessageSquare },
  { id: 'amplify',     label: 'Amplify',         namespace: 'AWS/AmplifyHosting',      icon: Layers },
  { id: 'cognito',     label: 'Cognito',         namespace: 'AWS/Cognito',             icon: UserCheck },
  { id: 'vpn',         label: 'VPN',             namespace: 'AWS/VPN',                 icon: Lock },
  { id: 'tgw',         label: 'Transit GW',      namespace: 'AWS/TransitGateway',      icon: Network },
  { id: 'dx',          label: 'Direct Connect',  namespace: 'AWS/DX',                  icon: Link2 },
  { id: 'elasticache', label: 'ElastiCache',     namespace: 'AWS/ElastiCache',         icon: Cpu },
  { id: 'cloudfront',  label: 'CloudFront',      namespace: 'AWS/CloudFront',          icon: Radio },
  { id: 'sns',         label: 'SNS',             namespace: 'AWS/SNS',                 icon: Bell },
  { id: 'eks',         label: 'EKS',             namespace: 'ContainerInsights',       icon: Package },
];

const ALARM_STATE = {
  OK:               { label: 'OK',       bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  ALARM:            { label: 'Alarma',   bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
  INSUFFICIENT_DATA:{ label: 'Sin data', bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
};

const PROVIDERS = [
  { id: 'aws',    label: 'Amazon AWS' },
  { id: 'gcp',    label: 'Google Cloud' },
  { id: 'huawei', label: 'Huawei Cloud' },
  { id: 'azure',  label: 'Microsoft Azure' },
];

const CHART_COLORS = [
  '#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6',
  '#8b5cf6','#ec4899','#14b8a6','#f97316','#84cc16',
  '#64748b','#a855f7','#0ea5e9','#d97706','#16a34a',
];

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtNum(v) {
  if (v == null) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}G`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return parseFloat(v.toFixed(3)).toString();
}
function fmtBytes(v) {
  if (v == null) return '—';
  if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)} GB`;
  if (v >= 1048576)    return `${(v / 1048576).toFixed(1)} MB`;
  if (v >= 1024)       return `${(v / 1024).toFixed(1)} KB`;
  return `${Math.round(v)} B`;
}
function fmtBps(v) {
  if (v == null) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)} Gbps`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} Mbps`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)} Kbps`;
  return `${Math.round(v)} bps`;
}
function fmtMetricValue(metricName, value) {
  if (value == null) return '—';
  if (/State$/i.test(metricName))                                  return null; // handled as status pill
  if (/Bytes$|DataIn$|DataOut$|Memory|Storage|ReadBytes|WriteBytes/i.test(metricName)) return fmtBytes(value);
  if (/Bps|Ingress|Egress/i.test(metricName))                     return fmtBps(value);
  if (/Utilization|Pct|Percent/i.test(metricName))                return `${value.toFixed(1)}%`;
  return fmtNum(value);
}

// Human-readable labels for common CloudWatch metric names
const METRIC_LABELS = {
  // EC2
  CPUUtilization:         'CPU',
  NetworkIn:              'Red entrante',
  NetworkOut:             'Red saliente',
  DiskReadBytes:          'Lectura disco',
  DiskWriteBytes:         'Escritura disco',
  StatusCheckFailed:      'Falla de estado',
  StatusCheckFailed_Instance: 'Falla instancia',
  StatusCheckFailed_System:   'Falla sistema',
  // RDS
  DatabaseConnections:    'Conexiones DB',
  FreeableMemory:         'Memoria libre',
  FreeStorageSpace:       'Almac. libre',
  ReadIOPS:               'IOPS lectura',
  WriteIOPS:              'IOPS escritura',
  ReadLatency:            'Latencia lectura',
  WriteLatency:           'Latencia escritura',
  // Lambda
  Invocations:            'Invocaciones',
  Errors:                 'Errores',
  Duration:               'Duración',
  Throttles:              'Throttles',
  ConcurrentExecutions:   'Ejecuciones concurrentes',
  // ELB
  RequestCount:           'Solicitudes',
  TargetResponseTime:     'Tiempo respuesta',
  HTTPCode_Target_5XX_Count: 'Errores 5XX',
  HTTPCode_Target_4XX_Count: 'Errores 4XX',
  HealthyHostCount:       'Hosts saludables',
  UnHealthyHostCount:     'Hosts no saludables',
  // VPN
  TunnelState:            'Estado del túnel',
  TunnelDataIn:           'Datos entrantes',
  TunnelDataOut:          'Datos salientes',
  // Transit Gateway
  BytesIn:                'Bytes entrantes',
  BytesOut:               'Bytes salientes',
  PacketsIn:              'Paquetes entrantes',
  PacketsOut:             'Paquetes salientes',
  PacketDropCountBlackhole: 'Descartados (blackhole)',
  PacketDropCountNoRoute:   'Descartados (sin ruta)',
  // Direct Connect
  ConnectionState:        'Estado de conexión',
  ConnectionBpsIngress:   'BW entrante',
  ConnectionBpsEgress:    'BW saliente',
  ConnectionErrorCount:   'Errores',
  // SQS
  NumberOfMessagesSent:     'Mensajes enviados',
  NumberOfMessagesReceived: 'Mensajes recibidos',
  ApproximateNumberOfMessagesVisible: 'Mensajes en cola',
  // DynamoDB
  ConsumedReadCapacityUnits:  'RCU consumidas',
  ConsumedWriteCapacityUnits: 'WCU consumidas',
  // S3
  BucketSizeBytes:        'Tamaño del bucket',
  NumberOfObjects:        'Objetos',
  // API Gateway
  Count:                  'Solicitudes',
  Latency:                'Latencia',
  '4XXError':             'Errores 4XX',
  '5XXError':             'Errores 5XX',
  // ElastiCache
  CurrConnections:        'Conexiones activas',
  CacheHits:              'Aciertos de caché',
  CacheMisses:            'Fallos de caché',
  // CloudFront
  BytesDownloaded:        'Bytes descargados',
  BytesUploaded:          'Bytes subidos',
  '4xxErrorRate':         'Tasa error 4XX',
  '5xxErrorRate':         'Tasa error 5XX',
  TotalErrorRate:         'Tasa error total',
  // SNS
  NumberOfMessagesPublished:        'Mensajes publicados',
  NumberOfNotificationsDelivered:   'Notificaciones entregadas',
  NumberOfNotificationsFailed:      'Notificaciones fallidas',
  // Container Insights (EKS)
  node_cpu_utilization:    'CPU nodos',
  node_memory_utilization: 'Memoria nodos',
  pod_cpu_utilization:     'CPU pods',
  pod_memory_utilization:  'Memoria pods',
};

// ── SparklineSVG — mini time-series chart ────────────────────────────────────
function SparklineSVG({ pts, lineColor = 'var(--ink-3)' }) {
  if (!pts || pts.length < 2) return null;
  const W = 56, H = 18;
  const vals = pts.map(p => p.v);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const points = pts.map((p, i) => {
    const x = (i / (pts.length - 1)) * (W - 2) + 1;
    const y = H - 1 - ((p.v - min) / range) * (H - 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display:'block', flexShrink:0, overflow:'visible' }}>
      <polyline points={points} fill="none" stroke={lineColor}
        strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── MetricRow — label / value + sparkline + proportional bar ─────────────────
function MetricRow({ metricName, label, rawValue, maxValue, color, sparkPts }) {
  const displayLabel  = label || METRIC_LABELS[metricName] || metricName;
  const isStatus      = /State$/i.test(metricName);
  const isUtilization = /Utilization/i.test(metricName);

  if (isStatus) {
    const active   = rawValue != null && rawValue >= 0.5;
    const dotColor = rawValue == null ? 'var(--ink-3)' : (active ? 'var(--green)' : 'var(--red)');
    const text     = rawValue == null ? 'Sin datos' : (active ? 'Activo' : 'Inactivo');
    return (
      <div style={{ marginBottom:6, display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11, lineHeight:'15px' }}>
        <span style={{ color:'var(--ink-3)' }}>{displayLabel}</span>
        <span style={{ fontWeight:700, color: dotColor, display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background: dotColor, display:'inline-block', flexShrink:0 }} />
          {text}
        </span>
      </div>
    );
  }

  // Trend from sparkline data (compare last third vs first third)
  let trendPct = null, sparkColor = 'var(--ink-3)';
  if (sparkPts && sparkPts.length >= 4) {
    const vals = sparkPts.map(p => p.v);
    const third = Math.max(1, Math.floor(vals.length / 3));
    const firstAvg = vals.slice(0, third).reduce((s, v) => s + v, 0) / third;
    const lastAvg  = vals.slice(-third).reduce((s, v) => s + v, 0) / third;
    const pct = firstAvg > 0 ? Math.round(((lastAvg - firstAvg) / firstAvg) * 100) : 0;
    if (Math.abs(pct) >= 5) {
      trendPct = pct;
      // Higher = worse for errors/throttles/misses; higher = worse for utilization
      const isGoodIfLow = /Utilization|Error|Failed|Throttle|Miss|Unhealthy/i.test(metricName);
      sparkColor = (pct > 0 === isGoodIfLow) ? 'var(--red)' : 'var(--green)';
    }
  }

  // Threshold coloring for utilization metrics (CPU, Memory)
  const thresholdColor = (isUtilization && rawValue != null)
    ? (rawValue > 80 ? 'var(--red)' : rawValue > 60 ? 'var(--amber)' : 'var(--green)')
    : null;
  const barColor   = thresholdColor || color || 'var(--accent)';
  const valueColor = thresholdColor || 'var(--ink)';

  const displayValue = fmtMetricValue(metricName, rawValue);
  const pct = (maxValue > 0 && rawValue != null && rawValue > 0)
    ? Math.min((rawValue / maxValue) * 100, 100) : 0;

  const hasSpark = sparkPts && sparkPts.length >= 2;

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11, lineHeight:'15px', gap:4 }}>
        <span style={{ color:'var(--ink-3)', flexShrink:0 }}>{displayLabel}</span>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          {hasSpark && <SparklineSVG pts={sparkPts} lineColor={sparkColor} />}
          <div style={{ textAlign:'right' }}>
            <div style={{ fontWeight:600, fontFamily:'monospace', color: valueColor }}>{displayValue}</div>
            {trendPct !== null && (
              <div style={{ fontSize:9, fontWeight:700, color: sparkColor, lineHeight:'11px' }}>
                {trendPct > 0 ? '↑' : '↓'}{Math.abs(trendPct)}%
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ height:2, background:'var(--bg)', borderRadius:2, overflow:'hidden', marginTop:3 }}>
        <div style={{ width:`${pct}%`, height:'100%', background: barColor, borderRadius:2, transition:'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ── EnvBadge — inline environment selector per resource ──────────────────────
const ENV_BADGE = {
  PROD: { bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  TEST: { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  DESA: { bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)' },
};
function EnvBadge({ resourceId, current, onSetEnv }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const s = current ? (ENV_BADGE[current.toUpperCase()] || { bg:'var(--bg)', color:'var(--ink-2)', border:'var(--border)' }) : null;
  return (
    <div ref={ref} style={{ position:'relative', flexShrink:0, marginLeft:4 }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:3, cursor:'pointer',
          lineHeight:'14px', border: s ? `1px solid ${s.border}` : '1px dashed var(--border-2)',
          background: s ? s.bg : 'transparent', color: s ? s.color : 'var(--ink-3)' }}>
        {current || '+ env'}
      </button>
      {open && (
        <div style={{ position:'absolute', right:0, top:'100%', marginTop:2, background:'var(--surface)',
          border:'1px solid var(--border)', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.08)',
          zIndex:200, minWidth:80 }}>
          {['PROD','TEST','DESA'].map(env => (
            <button key={env} onClick={(e) => { e.stopPropagation(); onSetEnv(resourceId, env); setOpen(false); }}
              style={{ display:'block', width:'100%', padding:'5px 10px', textAlign:'left',
                background: current === env ? 'var(--bg)' : 'none', border:'none',
                cursor:'pointer', fontSize:12, color:'var(--ink-2)', fontWeight: current === env ? 700 : 400 }}>
              {env}
            </button>
          ))}
          {current && (
            <button onClick={(e) => { e.stopPropagation(); onSetEnv(resourceId, null); setOpen(false); }}
              style={{ display:'block', width:'100%', padding:'5px 10px', textAlign:'left',
                background:'none', border:'none', borderTop:'1px solid var(--bg)',
                cursor:'pointer', fontSize:11, color:'var(--ink-3)' }}>
              Quitar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── ResourceCard ──────────────────────────────────────────────────────────────
function ResourceCard({ resource, metricNames, meta, color, maxValues, sparkData, envOverride, onSetEnv }) {
  // meta may be an object {name,type,state,privateIp,publicIp,az,cpuCores} or undefined
  const displayName = meta?.name || resource.id;
  const showId = meta?.name && meta.name !== resource.id;
  const stateColor = meta?.state === 'running' ? 'var(--green)' : meta?.state ? 'var(--ink-2)' : null;
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', background:'var(--surface)',
      borderLeft: color ? `3px solid ${color}` : '1px solid var(--border)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:2 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--ink)',
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', flex:1, minWidth:0 }} title={displayName}>
          {displayName}
        </div>
        {onSetEnv && <EnvBadge resourceId={resource.id} current={envOverride} onSetEnv={onSetEnv} />}
      </div>
      {showId && (
        <div style={{ fontSize:10, color:'var(--ink-3)', fontFamily:'monospace',
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:2 }}>
          {resource.id}
        </div>
      )}
      {meta && (meta.type || meta.state || meta.privateIp) && (
        <div style={{ marginBottom:8, display:'flex', flexDirection:'column', gap:2 }}>
          {(meta.type || meta.state) && (
            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
              {meta.type && (
                <span style={{ fontSize:10, background:'var(--bg)', border:'1px solid var(--border)',
                  borderRadius:4, padding:'1px 6px', color:'var(--ink-2)', fontFamily:'monospace' }}>
                  {meta.type}
                </span>
              )}
              {meta.state && (
                <span style={{ fontSize:10, fontWeight:600, color: stateColor }}>
                  ● {meta.state}
                </span>
              )}
              {meta.cpuCores != null && (
                <span style={{ fontSize:10, color:'var(--ink-2)' }}>{meta.cpuCores} vCPU</span>
              )}
            </div>
          )}
          {(meta.privateIp || meta.az) && (
            <div style={{ fontSize:10, color:'var(--ink-2)', fontFamily:'monospace' }}>
              {meta.privateIp && <span>{meta.privateIp}</span>}
              {meta.privateIp && meta.az && <span style={{ color:'var(--border-2)' }}> · </span>}
              {meta.az && <span>{meta.az}</span>}
            </div>
          )}
        </div>
      )}
      {!meta && <div style={{ marginBottom:10 }} />}
      {metricNames.map(m => (
        <MetricRow key={m} metricName={m} rawValue={resource.metrics?.[m]}
          maxValue={maxValues?.[m]} color={color} sparkPts={sparkData?.[m]} />
      ))}
    </div>
  );
}

// ── TopNPanel — top 5 resources by key metrics ────────────────────────────────
function TopNPanel({ resources, names, metricNames }) {
  const topMetrics = metricNames.filter(m => !(/State$/i.test(m))).slice(0, 2);
  if (resources.length < 3 || topMetrics.length === 0) return null;

  const top5 = (metric) =>
    [...resources]
      .filter(r => r.metrics?.[metric] != null)
      .sort((a, b) => (b.metrics[metric] || 0) - (a.metrics[metric] || 0))
      .slice(0, 5);

  return (
    <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
      {topMetrics.map(metric => {
        const topList = top5(metric);
        if (topList.length === 0) return null;
        const maxVal = topList[0].metrics[metric] || 1;
        const metricLabel = METRIC_LABELS[metric] || metric;
        return (
          <div key={metric} style={{ flex:1, minWidth:240, border:'1px solid var(--border)', borderRadius:8, padding:'12px 16px', background:'var(--surface)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ink-2)', textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>
              Top 5 — {metricLabel}
            </div>
            {topList.map((r, i) => {
              const label = names?.[r.id]?.name || r.id;
              const val   = r.metrics[metric];
              const pct   = Math.min((val / maxVal) * 100, 100);
              const isUtil = /Utilization/i.test(metric);
              const barColor = isUtil ? (val > 80 ? 'var(--red)' : val > 60 ? 'var(--amber)' : 'var(--green)') : 'var(--accent)';
              return (
                <div key={r.id} style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:2 }}>
                    <span style={{ color:'var(--ink-2)', fontWeight: i === 0 ? 700 : 400,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'65%' }}>
                      {i + 1}. {label}
                    </span>
                    <span style={{ fontFamily:'monospace', fontWeight:600, color: isUtil ? barColor : 'var(--ink)', flexShrink:0 }}>
                      {fmtMetricValue(metric, val)}
                    </span>
                  </div>
                  <div style={{ height:3, background:'var(--bg)', borderRadius:2 }}>
                    <div style={{ width:`${pct}%`, height:'100%', background: barColor, borderRadius:2 }} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── ServicePanel ──────────────────────────────────────────────────────────────
const HOURS_OPTIONS = [
  { value: 1,   label: '1h' },
  { value: 6,   label: '6h' },
  { value: 24,  label: '24h' },
  { value: 168, label: '7d' },
];

function ServicePanel({ namespace, token, accountId }) {
  const [data, setData]           = useState(null);
  const [names, setNames]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [envOverrides, setEnvOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`itil_envmap_${namespace}`) || '{}'); } catch { return {}; }
  });
  const [error, setError]         = useState(null);
  const [syncing, setSyncing]     = useState(false);
  const [syncMsg, setSyncMsg]     = useState(null);
  const [search, setSearch]       = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [hours, setHours]         = useState(1);
  const [sortBy, setSortBy]       = useState('none');
  const [sparklines, setSparklines]   = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshSecs, setRefreshSecs] = useState(300);
  const [countdown, setCountdown]     = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const handleSetEnv = useCallback((resourceId, env) => {
    setEnvOverrides(prev => {
      const next = { ...prev };
      if (env === null) delete next[resourceId]; else next[resourceId] = env;
      localStorage.setItem(`itil_envmap_${namespace}`, JSON.stringify(next));
      return next;
    });
  }, [namespace]);

  useEffect(() => {
    setLoading(true); setError(null); setData(null); setNames({});
    const headers = { Authorization: `Bearer ${token}` };
    const acctQs = accountId ? `&accountId=${accountId}` : '';
    const overviewP = fetch(
      `${API_URL}/api/monitoring/aws/overview?namespace=${encodeURIComponent(namespace)}&hours=${hours}${acctQs}`,
      { headers }
    ).then(r => r.json()).then(d => { if (d.error) throw new Error(d.error); setData(d); });
    const namesP = namespace === 'AWS/EC2'
      ? fetch(`${API_URL}/api/monitoring/aws/resource-names?namespace=AWS/EC2${acctQs}`, { headers })
          .then(r => r.json()).then(d => setNames(d)).catch(() => {})
      : Promise.resolve();
    Promise.all([overviewP, namesP])
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [namespace, token, hours, refreshTick]);

  // Fetch batch sparklines after overview loads
  useEffect(() => {
    if (!data?.resources?.length) { setSparklines({}); return; }
    const ctrl = new AbortController();
    fetch(`${API_URL}/api/monitoring/aws/sparklines`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ namespace: data.namespace, resourceIds: data.resources.map(r => r.id), hours, ...(accountId ? { accountId } : {}) }),
      signal: ctrl.signal,
    })
      .then(r => r.json())
      .then(d => setSparklines(d && typeof d === 'object' && !Array.isArray(d) ? d : {}))
      .catch(() => {});
    return () => ctrl.abort();
  }, [data, token, hours]);

  // Auto-refresh countdown
  useEffect(() => {
    if (!autoRefresh) { setCountdown(0); return; }
    setCountdown(refreshSecs);
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { setRefreshTick(t => t + 1); return refreshSecs; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [autoRefresh, refreshSecs]);

  const syncToAssets = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/monitoring/aws/sync-assets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setSyncMsg({ ok: true, inserted: d.inserted, updated: d.updated });
    } catch (e) {
      setSyncMsg({ ok: false, msg: e.message });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Cargando métricas…</div>;
  if (error)   return <div style={{ padding:24, color:'var(--red)', fontSize:13 }}>Error: {error}</div>;
  if (!data?.resources?.length) return (
    <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
      Sin recursos encontrados en {namespace}
    </div>
  );

  // Assign colors + compute per-metric max for bar normalization
  const colorMap = {};
  data.resources.forEach((r, i) => { colorMap[r.id] = CHART_COLORS[i % CHART_COLORS.length]; });
  const maxValues = {};
  for (const mName of data.metric_names) {
    const vals = data.resources.map(r => r.metrics?.[mName]).filter(v => v != null && v > 0);
    maxValues[mName] = vals.length ? Math.max(...vals) : 0;
  }

  // Filter resources by search and state
  const filtered = data.resources.filter(r => {
    const meta = names[r.id];
    const label = (meta?.name || r.id).toLowerCase();
    const matchSearch = !search || label.includes(search.toLowerCase()) || r.id.toLowerCase().includes(search.toLowerCase());
    const matchState  = stateFilter === 'all' || meta?.state === stateFilter;
    return matchSearch && matchState;
  });

  // Sort filtered resources
  const sortedResources = [...filtered].sort((a, b) => {
    if (sortBy === 'cpu_desc')  return (b.metrics?.CPUUtilization  || 0) - (a.metrics?.CPUUtilization  || 0);
    if (sortBy === 'net_desc')  return ((b.metrics?.NetworkIn || 0) + (b.metrics?.NetworkOut || 0)) -
                                        ((a.metrics?.NetworkIn || 0) + (a.metrics?.NetworkOut || 0));
    if (sortBy === 'mem_desc')  return (b.metrics?.FreeableMemory  != null && a.metrics?.FreeableMemory != null)
                                  ? (a.metrics.FreeableMemory - b.metrics.FreeableMemory) : 0;
    if (sortBy === 'name')      return (names[a.id]?.name || a.id).localeCompare(names[b.id]?.name || b.id);
    return 0;
  });

  // Group by environment — AWS tag overridden by manual selection (always shown)
  const ENV_ORDER = ['prod','production','prd'];
  const effectiveEnv = (r) => envOverrides[r.id] || names[r.id]?.environment || null;
  const envGroups = sortedResources.reduce((acc, r) => {
    const env = effectiveEnv(r) || 'Sin ambiente';
    if (!acc[env]) acc[env] = [];
    acc[env].push(r);
    return acc;
  }, {});

  const hoursLabel = hours === 168 ? '7 días' : hours === 24 ? '24 horas' : hours === 6 ? '6 horas' : '1 hora';

  return (
    <div>
      {/* Time range selector + auto-refresh */}
      <div style={{ display:'flex', gap:4, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
        <span style={{ fontSize:12, color:'var(--ink-2)', marginRight:4 }}>Rango:</span>
        {HOURS_OPTIONS.map(opt => (
          <button key={opt.value} onClick={() => setHours(opt.value)}
            style={{ padding:'4px 12px', fontSize:12, borderRadius:6, border:'1px solid var(--border-2)',
              background: hours === opt.value ? 'var(--accent)' : '#fff',
              color: hours === opt.value ? '#fff' : 'var(--ink-2)',
              fontWeight: hours === opt.value ? 700 : 400, cursor:'pointer' }}>
            {opt.label}
          </button>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto' }}>
          {autoRefresh && countdown > 0 && (
            <span style={{ fontSize:11, color:'var(--ink-3)', fontFamily:'monospace', minWidth:36 }}>
              {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
            </span>
          )}
          <select value={refreshSecs} onChange={e => setRefreshSecs(Number(e.target.value))}
            style={{ fontSize:11, padding:'3px 8px', border:'1px solid var(--border-2)', borderRadius:5,
              background:'var(--surface)', color:'var(--ink-2)', cursor:'pointer' }}>
            <option value={60}>1 min</option>
            <option value={300}>5 min</option>
            <option value={600}>10 min</option>
          </select>
          <button onClick={() => setAutoRefresh(a => !a)}
            style={{ padding:'4px 10px', fontSize:11, borderRadius:5, border:'1px solid var(--border-2)',
              background: autoRefresh ? 'var(--accent)' : '#fff',
              color: autoRefresh ? '#fff' : 'var(--ink-2)',
              fontWeight: autoRefresh ? 700 : 400, cursor:'pointer' }}>
            {autoRefresh ? '⏸ Auto' : '▶ Auto'}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o ID…"
          style={{ flex:1, minWidth:180, padding:'6px 10px', border:'1px solid var(--border-2)', borderRadius:7, fontSize:13, color:'var(--ink)' }}
        />
        {namespace === 'AWS/EC2' && (
          <div style={{ display:'flex', gap:4 }}>
            {[['all','Todos'], ['running','Activos'], ['stopped','Detenidos']].map(([val, lbl]) => (
              <button key={val} onClick={() => setStateFilter(val)}
                style={{ padding:'5px 12px', fontSize:12, borderRadius:6, border:'1px solid var(--border-2)',
                  background: stateFilter === val ? '#f97316' : '#fff',
                  color: stateFilter === val ? '#fff' : 'var(--ink-2)',
                  fontWeight: stateFilter === val ? 700 : 400, cursor:'pointer' }}>
                {lbl}
              </button>
            ))}
          </div>
        )}
        {/* Sort selector */}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding:'5px 10px', fontSize:12, borderRadius:6, border:'1px solid var(--border-2)',
            background:'var(--surface)', color:'var(--ink-2)', cursor:'pointer' }}>
          <option value="none">Sin ordenar</option>
          {data.metric_names.includes('CPUUtilization') && <option value="cpu_desc">Mayor CPU</option>}
          {data.metric_names.some(m => m.startsWith('Network')) && <option value="net_desc">Mayor tráfico red</option>}
          {data.metric_names.includes('FreeableMemory') && <option value="mem_desc">Menor memoria libre</option>}
          <option value="name">Nombre A→Z</option>
        </select>
      </div>

      {/* Top N panel (EC2 + >2 resources) */}
      {!search && stateFilter === 'all' && data.resources.length > 2 && (
        <TopNPanel resources={data.resources} names={names} metricNames={data.metric_names} />
      )}

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <div style={{ fontSize:12, color:'var(--ink-2)' }}>
          {filtered.length} de {data.resources.length} recurso(s) · Promedio últimas {hoursLabel}
        </div>
        {namespace === 'AWS/EC2' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <button onClick={syncToAssets} disabled={syncing}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 14px',
                background:'var(--accent-dim)', border:'1px solid #c7d2fe', borderRadius:7,
                color:'var(--accent)', fontSize:12, fontWeight:600, cursor: syncing ? 'not-allowed' : 'pointer',
                opacity: syncing ? 0.7 : 1 }}>
              <RefreshCw size={12} />
              {syncing ? 'Sincronizando…' : 'Importar a Activos Virtuales'}
            </button>
            {syncMsg && syncMsg.ok && (
              <span style={{ fontSize:12, color:'var(--green)', display:'flex', alignItems:'center', gap:6 }}>
                ✓ {syncMsg.inserted} nuevos · {syncMsg.updated} actualizados
                <a href="/activos?tipo=virtual" style={{ color:'var(--accent)', textDecoration:'underline', fontSize:12 }}>
                  Ver activos
                </a>
              </span>
            )}
            {syncMsg && !syncMsg.ok && (
              <span style={{ fontSize:12, color:'var(--red)' }}>Error: {syncMsg.msg}</span>
            )}
          </div>
        )}
      </div>
      {filtered.length === 0 && (
        <div style={{ padding:'32px 0', textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
          Sin resultados para "{search || stateFilter}"
        </div>
      )}
      {Object.entries(envGroups)
        .sort(([a], [b]) => {
          if (a === 'Sin ambiente' && b !== 'Sin ambiente') return 1;
          if (b === 'Sin ambiente' && a !== 'Sin ambiente') return -1;
          const aP = ENV_ORDER.some(e => a.toLowerCase().includes(e));
          const bP = ENV_ORDER.some(e => b.toLowerCase().includes(e));
          if (aP && !bP) return -1; if (!aP && bP) return 1;
          return a.localeCompare(b);
        })
        .map(([env, resources]) => {
          const envKey = env.toUpperCase();
          const badgeStyle = ENV_BADGE[envKey] || null;
          return (
            <div key={env} style={{ marginBottom: 24 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10,
                paddingBottom:6, borderBottom:'1px solid var(--border)' }}>
                {badgeStyle && (
                  <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:3,
                    background: badgeStyle.bg, color: badgeStyle.color, border:`1px solid ${badgeStyle.border}` }}>
                    {envKey}
                  </span>
                )}
                <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:1, color: badgeStyle ? badgeStyle.color : 'var(--ink-3)' }}>
                  {badgeStyle ? '' : env}
                </span>
                <span style={{ fontSize:11, color:'var(--ink-3)' }}>({resources.length})</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:10 }}>
                {resources.map(r => (
                  <ResourceCard key={r.id} resource={r} metricNames={data.metric_names}
                    meta={names[r.id]} color={colorMap[r.id]} maxValues={maxValues}
                    sparkData={sparklines[r.id]}
                    envOverride={effectiveEnv(r)}
                    onSetEnv={handleSetEnv} />
                ))}
              </div>
            </div>
          );
        })
      }
    </div>
  );
}

// ── AlertsPanel — custom threshold alerts ─────────────────────────────────────
const NS_OPTIONS = [
  'AWS/EC2','AWS/RDS','AWS/Lambda','AWS/ECS','AWS/S3',
  'AWS/ApplicationELB','AWS/ApiGateway','AWS/DynamoDB','AWS/SQS',
  'AWS/ElastiCache','AWS/CloudFront','AWS/SNS','ContainerInsights',
];
const EMPTY_FORM = {
  name:'', namespace:'AWS/EC2', metric_name:'CPUUtilization',
  resource_id:'', operator:'>', threshold:'', duration_minutes:5, severity:'media',
};
function AlertsPanel({ token }) {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]    = useState(null); // null = new
  const [form, setForm]          = useState(EMPTY_FORM);
  const [saving, setSaving]      = useState(false);
  const [formError, setFormError] = useState(null);
  const [resources, setResources]   = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/monitoring/aws/alerts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setAlerts(Array.isArray(d) ? d : []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showModal || !form.namespace) return;
    setLoadingRes(true);
    setResources([]);
    fetch(`${API_URL}/api/monitoring/aws/resources?namespace=${encodeURIComponent(form.namespace)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setResources(Array.isArray(d) ? d : []))
      .catch(() => setResources([]))
      .finally(() => setLoadingRes(false));
  }, [form.namespace, showModal, token]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowModal(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ name: a.name, namespace: a.namespace, metric_name: a.metric_name,
              resource_id: a.resource_id || '', operator: a.operator,
              threshold: a.threshold, duration_minutes: a.duration_minutes, severity: a.severity });
    setFormError(null); setShowModal(true);
  };

  const save = async () => {
    if (!form.name || !form.metric_name || form.threshold === '') return setFormError('Completá todos los campos requeridos');
    setSaving(true); setFormError(null);
    try {
      const url    = editing ? `${API_URL}/api/monitoring/aws/alerts/${editing}` : `${API_URL}/api/monitoring/aws/alerts`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, threshold: parseFloat(form.threshold), duration_minutes: parseInt(form.duration_minutes) }),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setShowModal(false); load();
    } catch (e) { setFormError(e.message); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar esta alerta?')) return;
    await fetch(`${API_URL}/api/monitoring/aws/alerts/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };

  const toggle = async (a) => {
    await fetch(`${API_URL}/api/monitoring/aws/alerts/${a.id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...a, enabled: !a.enabled, resource_id: a.resource_id || '' }),
    });
    load();
  };

  const SEV_COLOR = { baja:'var(--accent)', media:'var(--amber)', alta:'var(--red)', critica:'var(--red)' };
  const SEV_BG    = { baja:'var(--accent-dim)', media:'var(--amber-dim)', alta:'var(--red-dim)', critica:'#450a0a' };
  const inputStyle = { width:'100%', padding:'7px 10px', border:'1px solid var(--border-2)',
    borderRadius:6, fontSize:13, boxSizing:'border-box' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:13, color:'var(--ink-2)' }}>
          Reglas personalizadas: cuando una métrica supera el umbral se crea un incidente automáticamente.
        </div>
        <button className="btn btn-primary" onClick={openNew} style={{ fontSize:13 }}>+ Nueva alerta</button>
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Cargando…</div>}
      {error   && <div style={{ padding:16, color:'var(--red)', fontSize:13 }}>Error: {error}</div>}

      {!loading && !error && !alerts.length && (
        <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
          No hay alertas configuradas. Creá una con el botón de arriba.
        </div>
      )}

      {!loading && alerts.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Alerta</th>
                  <th>Condición</th>
                  <th style={{ width:90 }}>Severidad</th>
                  <th style={{ width:80 }}>Estado</th>
                  <th style={{ width:110 }}>Último valor</th>
                  <th style={{ width:90 }}></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map(a => (
                  <tr key={a.id} style={{ opacity: a.enabled ? 1 : 0.5 }}>
                    <td>
                      <div style={{ fontWeight:500, fontSize:13 }}>{a.name}</div>
                      <div style={{ fontSize:11, color:'var(--ink-3)' }}>{a.namespace}</div>
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      <span style={{ fontFamily:'monospace', background:'var(--bg)', padding:'2px 6px', borderRadius:4 }}>
                        {a.metric_name} {a.operator} {a.threshold}
                      </span>
                      {a.resource_id && <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:2 }}>{a.resource_id}</div>}
                      <div style={{ fontSize:11, color:'var(--ink-3)' }}>por {a.duration_minutes} min</div>
                    </td>
                    <td>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4,
                        background: SEV_BG[a.severity] || 'var(--bg)',
                        color: SEV_COLOR[a.severity] || 'var(--ink-2)' }}>
                        {a.severity}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => toggle(a)} style={{
                        fontSize:11, padding:'2px 8px', borderRadius:4, cursor:'pointer', border:'none',
                        background: a.enabled ? 'var(--green-dim)' : 'var(--bg)',
                        color: a.enabled ? 'var(--green)' : 'var(--ink-2)', fontWeight:600 }}>
                        {a.enabled ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      {a.last_value != null ? parseFloat(a.last_value).toFixed(2) : '—'}
                      {a.last_triggered_at && (
                        <div style={{ fontSize:10, color:'var(--red)' }}>
                          Disparó {new Date(a.last_triggered_at).toLocaleString('es-AR')}
                        </div>
                      )}
                    </td>
                    <td style={{ display:'flex', gap:6 }}>
                      <button onClick={() => openEdit(a)} className="btn btn-secondary"
                        style={{ fontSize:11, padding:'3px 8px' }}>Editar</button>
                      <button onClick={() => del(a.id)}
                        style={{ fontSize:11, padding:'3px 8px', background:'none', border:'1px solid var(--red-dim)',
                          color:'var(--red)', borderRadius:6, cursor:'pointer' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'var(--surface)', borderRadius:10, padding:28, width:480, maxWidth:'95vw',
            maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700 }}>
              {editing ? 'Editar alerta' : 'Nueva alerta de monitoreo'}
            </h3>
            {formError && <div style={{ marginBottom:12, color:'var(--red)', fontSize:13 }}>{formError}</div>}

            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Nombre *</label>
                <input style={inputStyle} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ej: CPU alta en producción" />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Namespace *</label>
                  <select style={inputStyle} value={form.namespace}
                    onChange={e => setForm(f => ({ ...f, namespace: e.target.value }))}>
                    {NS_OPTIONS.map(ns => <option key={ns} value={ns}>{ns.replace('AWS/','')}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Métrica *</label>
                  <input style={inputStyle} value={form.metric_name}
                    onChange={e => setForm(f => ({ ...f, metric_name: e.target.value }))}
                    placeholder="CPUUtilization" />
                </div>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>
                  Recurso <span style={{ fontWeight:400, color:'var(--ink-3)' }}>(opcional — vacío = todos)</span>
                </label>
                {loadingRes ? (
                  <div style={{ fontSize:12, color:'var(--ink-3)', padding:'8px 0' }}>Cargando recursos…</div>
                ) : (
                  <select style={inputStyle} value={form.resource_id}
                    onChange={e => setForm(f => ({ ...f, resource_id: e.target.value }))}>
                    <option value="">— Todos los recursos —</option>
                    {resources.map(r => (
                      <option key={r.id} value={r.id}>{r.name || r.id}</option>
                    ))}
                  </select>
                )}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Operador</label>
                  <select style={inputStyle} value={form.operator}
                    onChange={e => setForm(f => ({ ...f, operator: e.target.value }))}>
                    {['>','>=','<','<=','='].map(op => <option key={op} value={op}>{op}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Umbral *</label>
                  <input style={inputStyle} type="number" value={form.threshold}
                    onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                    placeholder="85" />
                </div>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Duración (min)</label>
                  <input style={inputStyle} type="number" min={1} max={60} value={form.duration_minutes}
                    onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Severidad</label>
                <select style={inputStyle} value={form.severity}
                  onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                  {['baja','media','alta','critica'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── LogsPanel — CloudWatch Logs viewer ────────────────────────────────────────
const LOG_HOURS = [
  { v: 0.25, label: '15 min' }, { v: 1, label: '1 h' },
  { v: 6, label: '6 h' }, { v: 24, label: '24 h' },
];
const LOG_LEVEL_RE = /\b(ERROR|CRITICAL|FATAL|Exception)\b/i;
const LOG_WARN_RE  = /\b(WARN|WARNING)\b/i;
function LogsPanel({ token, accountId }) {
  const [groups, setGroups]       = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [selGroup, setSelGroup]   = useState('');
  const [events, setEvents]       = useState([]);
  const [loadingG, setLoadingG]   = useState(true);
  const [loadingE, setLoadingE]   = useState(false);
  const [errorG, setErrorG]       = useState(null);
  const [errorE, setErrorE]       = useState(null);
  const [hours, setHours]         = useState(1);
  const [filter, setFilter]       = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');

  useEffect(() => {
    setLoadingG(true); setErrorG(null);
    const acctQs = accountId ? `&accountId=${accountId}` : '';
    fetch(`${API_URL}/api/monitoring/aws/logs/groups?limit=50${acctQs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setGroups(Array.isArray(d) ? d : []); })
      .catch(e => setErrorG(e.message))
      .finally(() => setLoadingG(false));
  }, [token]);

  const loadEvents = useCallback((grp, h, flt) => {
    if (!grp) return;
    setLoadingE(true); setErrorE(null); setEvents([]);
    const params = new URLSearchParams({ logGroup: grp, hours: h, limit: 200 });
    if (flt) params.set('filter', flt);
    if (accountId) params.set('accountId', accountId);
    fetch(`${API_URL}/api/monitoring/aws/logs/events?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setEvents(Array.isArray(d) ? d : []); })
      .catch(e => setErrorE(e.message))
      .finally(() => setLoadingE(false));
  }, [token]);

  useEffect(() => { if (selGroup) loadEvents(selGroup, hours, appliedFilter); }, [selGroup, hours, appliedFilter, loadEvents]);

  const filteredGroups = groups.filter(g =>
    !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const fmtTs = (ms) => new Date(ms).toLocaleString('es-AR', { hour12:false });

  const logColor = (msg) =>
    LOG_LEVEL_RE.test(msg) ? 'var(--red)' :
    LOG_WARN_RE.test(msg)  ? 'var(--amber)' : 'var(--ink-2)';

  return (
    <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:20, minHeight:400 }}>
      {/* Left: group list */}
      <div className="card" style={{ padding:0, overflowY:'auto', maxHeight:640 }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Log Groups</div>
          <input
            style={{ width:'100%', padding:'6px 10px', border:'1px solid var(--border-2)',
              borderRadius:6, fontSize:12, boxSizing:'border-box' }}
            placeholder="Buscar grupo…"
            value={groupSearch}
            onChange={e => setGroupSearch(e.target.value)}
          />
        </div>
        {loadingG && <div style={{ padding:20, textAlign:'center', color:'var(--ink-3)', fontSize:12 }}>Cargando…</div>}
        {errorG   && <div style={{ padding:12, color:'var(--red)', fontSize:12 }}>{errorG}</div>}
        {!loadingG && !errorG && filteredGroups.map(g => (
          <div key={g.name}
            onClick={() => setSelGroup(g.name)}
            style={{
              padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--bg)',
              background: selGroup === g.name ? 'var(--accent-dim)' : 'transparent',
              borderLeft: selGroup === g.name ? '3px solid #6366f1' : '3px solid transparent',
            }}>
            <div style={{ fontSize:12, fontWeight: selGroup===g.name ? 600 : 400,
              color: selGroup===g.name ? 'var(--accent)' : 'var(--ink-2)', wordBreak:'break-all' }}>
              {g.name}
            </div>
            {g.retentionDays && (
              <div style={{ fontSize:10, color:'var(--ink-3)', marginTop:2 }}>Retención: {g.retentionDays}d</div>
            )}
          </div>
        ))}
        {!loadingG && !errorG && !filteredGroups.length && (
          <div style={{ padding:20, textAlign:'center', color:'var(--ink-3)', fontSize:12 }}>Sin grupos</div>
        )}
      </div>

      {/* Right: events */}
      <div>
        {!selGroup && (
          <div style={{ padding:60, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
            Seleccioná un Log Group para ver los eventos
          </div>
        )}
        {selGroup && (
          <>
            {/* Controls */}
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
              <div style={{ display:'flex', gap:4 }}>
                {LOG_HOURS.map(h => (
                  <button key={h.v} onClick={() => setHours(h.v)}
                    style={{ padding:'5px 12px', fontSize:12, border:'1px solid',
                      borderColor: hours===h.v ? 'var(--accent)' : 'var(--border-2)',
                      background: hours===h.v ? 'var(--accent)' : '#fff',
                      color: hours===h.v ? '#fff' : 'var(--ink-2)',
                      borderRadius:6, cursor:'pointer' }}>
                    {h.label}
                  </button>
                ))}
              </div>
              <input
                style={{ flex:1, minWidth:120, padding:'5px 10px', border:'1px solid var(--border-2)',
                  borderRadius:6, fontSize:12 }}
                placeholder="Filter pattern (ej: ERROR)"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                onKeyDown={e => e.key==='Enter' && setAppliedFilter(filter)}
              />
              <button onClick={() => setAppliedFilter(filter)} className="btn btn-secondary"
                style={{ fontSize:12, padding:'5px 12px' }}>Filtrar</button>
              <button onClick={() => loadEvents(selGroup, hours, appliedFilter)} className="btn btn-secondary"
                style={{ fontSize:12, padding:'5px 12px' }}>↺</button>
            </div>

            {loadingE && <div style={{ padding:32, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Cargando eventos…</div>}
            {errorE   && <div style={{ padding:16, color:'var(--red)', fontSize:13 }}>{errorE}</div>}

            {!loadingE && !errorE && !events.length && (
              <div style={{ padding:40, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
                Sin eventos en el período seleccionado
              </div>
            )}

            {!loadingE && events.length > 0 && (
              <div className="card" style={{ padding:0, overflowY:'auto', maxHeight:560, fontFamily:'monospace' }}>
                <div style={{ fontSize:11, color:'var(--ink-3)', padding:'6px 14px', borderBottom:'1px solid var(--border)',
                  background:'var(--bg-subtle)' }}>
                  {events.length} eventos · {selGroup}
                </div>
                {events.map((e, i) => (
                  <div key={i} style={{
                    padding:'6px 14px', borderBottom:'1px solid var(--bg)',
                    display:'grid', gridTemplateColumns:'160px 1fr',
                    background: i%2===0 ? '#fff' : 'var(--bg-subtle)',
                  }}>
                    <div style={{ fontSize:11, color:'var(--ink-3)', paddingRight:10, whiteSpace:'nowrap' }}>
                      {fmtTs(e.timestamp)}
                    </div>
                    <div style={{ fontSize:12, color: logColor(e.message), wordBreak:'break-all', whiteSpace:'pre-wrap' }}>
                      {e.message}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── NocDashboard ──────────────────────────────────────────────────────────────
const SERVICE_DISPLAY = {
  'AWS/EC2':              { label: 'EC2',            icon: Server },
  'AWS/RDS':              { label: 'RDS',            icon: Database },
  'AWS/Lambda':           { label: 'Lambda',         icon: Zap },
  'AWS/ECS':              { label: 'ECS',            icon: Package },
  'AWS/S3':               { label: 'S3',             icon: Archive },
  'AWS/ApplicationELB':   { label: 'ELB',            icon: Shuffle },
  'AWS/ApiGateway':       { label: 'API GW',         icon: Globe },
  'AWS/DynamoDB':         { label: 'DynamoDB',       icon: HardDrive },
  'AWS/SQS':              { label: 'SQS',            icon: MessageSquare },
  'AWS/ElastiCache':      { label: 'ElastiCache',    icon: Cpu },
  'AWS/CloudFront':       { label: 'CloudFront',     icon: Radio },
  'AWS/SNS':              { label: 'SNS',            icon: Bell },
  'ContainerInsights':    { label: 'EKS',            icon: Package },
  'AWS/VPN':              { label: 'VPN',            icon: Lock },
  'AWS/TransitGateway':   { label: 'Transit GW',     icon: Network },
  'AWS/DX':               { label: 'Direct Connect', icon: Link2 },
};

function NocDashboard({ token, accountId }) {
  const [alarms, setAlarms]   = useState([]);
  const [alerts, setAlerts]   = useState([]);
  const [counts, setCounts]   = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const h = { Authorization: `Bearer ${token}` };
    const acctQs = accountId ? `?accountId=${accountId}` : '';
    Promise.allSettled([
      fetch(`${API_URL}/api/monitoring/aws/alarms${acctQs}`,          { headers: h }).then(r => r.json()),
      fetch(`${API_URL}/api/monitoring/aws/alerts`,                    { headers: h }).then(r => r.json()),
      fetch(`${API_URL}/api/monitoring/aws/resource-counts${acctQs}`, { headers: h }).then(r => r.json()),
    ]).then(([alarmsR, alertsR, countsR]) => {
      if (alarmsR.status === 'fulfilled' && Array.isArray(alarmsR.value)) setAlarms(alarmsR.value);
      if (alertsR.status === 'fulfilled' && Array.isArray(alertsR.value)) setAlerts(alertsR.value);
      if (countsR.status === 'fulfilled' && typeof countsR.value === 'object' && countsR.value !== null)
        setCounts(countsR.value);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Cargando dashboard…</div>;
  if (error)   return <div style={{ padding:24, color:'var(--red)', fontSize:13 }}>Error: {error}</div>;

  const now = Date.now();
  const totalResources   = Object.values(counts).reduce((s, v) => s + (v || 0), 0);
  const activeAlarms     = alarms.filter(a => a.state === 'ALARM').length;
  const triggeredAlerts  = alerts.filter(a => a.last_triggered_at &&
    (now - new Date(a.last_triggered_at).getTime()) < 86400000);
  const servicesWithData = Object.values(counts).filter(v => (v || 0) > 0).length;
  const servicesNoData   = Object.keys(SERVICE_DISPLAY).length - servicesWithData;

  const alarmableAlarms  = alarms.filter(a => a.state === 'ALARM' || a.state === 'INSUFFICIENT_DATA');
  const sortedTriggered  = [...triggeredAlerts].sort(
    (a, b) => new Date(b.last_triggered_at) - new Date(a.last_triggered_at)
  );
  const SEV_COLOR = { baja:'var(--accent)', media:'var(--amber)', alta:'var(--red)', critica:'var(--red)' };
  const SEV_BG    = { baja:'var(--accent-dim)', media:'var(--amber-dim)', alta:'var(--red-dim)', critica:'var(--red-dim)' };

  return (
    <div>
      {/* Top-right refresh */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={load}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px',
            background:'var(--bg)', border:'1px solid var(--border-2)', borderRadius:7,
            fontSize:12, color:'var(--ink-2)', cursor:'pointer', fontWeight:500 }}>
          <RefreshCw size={12} />
          Actualizar
        </button>
      </div>

      {/* Stat strip */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        {[
          { label:'Recursos monitoreados', value: totalResources,         color:'var(--ink)', bg:'var(--bg-subtle)', border:'var(--border)' },
          { label:'Alarmas activas',        value: activeAlarms,          color: activeAlarms    > 0 ? 'var(--red)' : 'var(--ink)', bg: activeAlarms    > 0 ? 'var(--red-dim)' : 'var(--bg-subtle)', border: activeAlarms    > 0 ? 'var(--red-dim)' : 'var(--border)' },
          { label:'Alertas disparadas (24h)',value: triggeredAlerts.length,color: triggeredAlerts.length > 0 ? 'var(--amber)' : 'var(--ink)', bg: triggeredAlerts.length > 0 ? 'var(--amber-dim)' : 'var(--bg-subtle)', border: triggeredAlerts.length > 0 ? 'var(--amber-dim)' : 'var(--border)' },
          { label:'Servicios sin datos',    value: servicesNoData < 0 ? 0 : servicesNoData, color:'var(--ink-2)', bg:'var(--bg-subtle)', border:'var(--border)' },
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} style={{ padding:'14px 20px', background:bg, borderRadius:8,
            border:`1px solid ${border}`, minWidth:120, flex:1 }}>
            <div style={{ fontSize:28, fontWeight:800, color }}>{value}</div>
            <div style={{ fontSize:11, color:'var(--ink-2)', marginTop:3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Alarms table */}
      {alarmableAlarms.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--ink)' }}>
            Alarmas CloudWatch
          </div>
          <div className="card" style={{ padding:0 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Alarma</th>
                    <th style={{ width:100 }}>Estado</th>
                    <th style={{ width:180 }}>Métrica</th>
                    <th style={{ width:160 }}>Actualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {alarmableAlarms.map((a, i) => {
                    const st = ALARM_STATE[a.state] || ALARM_STATE.INSUFFICIENT_DATA;
                    return (
                      <tr key={i}>
                        <td>
                          <div style={{ fontWeight:500, fontSize:13 }}>{a.name}</div>
                          {a.description && <div style={{ fontSize:11, color:'var(--ink-3)' }}>{a.description}</div>}
                        </td>
                        <td>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4,
                            background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>
                            {st.label}
                          </span>
                        </td>
                        <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                          {a.namespace ? a.namespace.replace('AWS/','') + ' · ' : ''}{a.metric}
                        </td>
                        <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                          {a.updated_at ? new Date(a.updated_at).toLocaleString('es-AR') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Triggered custom alerts */}
      {sortedTriggered.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--ink)' }}>
            Alertas personalizadas disparadas (últimas 24 h)
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {sortedTriggered.map(a => (
              <div key={a.id} style={{ border:'1px solid var(--border)', borderRadius:8,
                padding:'12px 16px', background:'var(--surface)', minWidth:200, flex:1, maxWidth:320 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{a.name}</div>
                <div style={{ fontSize:11, color:'var(--ink-2)', fontFamily:'monospace',
                  background:'var(--bg)', borderRadius:4, padding:'2px 6px', display:'inline-block', marginBottom:6 }}>
                  {a.metric_name} {a.operator} {a.threshold}
                </div>
                {a.last_value != null && (
                  <div style={{ fontSize:12, color:'var(--ink-2)', marginBottom:4 }}>
                    Último valor: <strong>{parseFloat(a.last_value).toFixed(2)}</strong>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:4 }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:4,
                    background: SEV_BG[a.severity] || 'var(--bg)',
                    color: SEV_COLOR[a.severity] || 'var(--ink-2)' }}>
                    {a.severity}
                  </span>
                  <span style={{ fontSize:10, color:'var(--ink-3)' }}>
                    {new Date(a.last_triggered_at).toLocaleString('es-AR')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service health grid */}
      {Object.keys(counts).some(k => (counts[k] || 0) > 0) && (
        <div>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--ink)' }}>
            Servicios monitoreados
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(130px, 1fr))', gap:10 }}>
            {Object.entries(counts)
              .filter(([, v]) => (v || 0) > 0)
              .map(([ns, cnt]) => {
                const svc = SERVICE_DISPLAY[ns] || { label: ns.replace('AWS/',''), icon: Cloud };
                const SvcIcon = svc.icon;
                return (
                  <div key={ns} style={{ border:'1px solid #d1fae5', borderRadius:8,
                    padding:'12px 14px', background:'var(--green-dim)', display:'flex',
                    flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                      width:36, height:36, borderRadius:8, background:'var(--amber-dim)' }}>
                      <SvcIcon size={18} style={{ color:'#f97316' }} />
                    </div>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--ink)' }}>{svc.label}</div>
                    <div style={{ fontSize:11, color:'var(--green)', fontWeight:700 }}>
                      {cnt} recurso{cnt !== 1 ? 's' : ''}
                    </div>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--green)', marginTop:2 }} />
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {totalResources === 0 && alarms.length === 0 && (
        <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
          Sin datos de monitoreo disponibles aún.
        </div>
      )}
    </div>
  );
}

// ── AlarmsPanel ───────────────────────────────────────────────────────────────
function AlarmsPanel({ token, accountId }) {
  const [alarms, setAlarms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const qs = accountId ? `?accountId=${accountId}` : '';
    fetch(`${API_URL}/api/monitoring/aws/alarms${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setAlarms(Array.isArray(d) ? d : []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Cargando alarmas…</div>;
  if (error)   return <div style={{ padding:24, color:'var(--red)', fontSize:13 }}>Error: {error}</div>;
  if (!alarms.length) return (
    <div style={{ padding:48, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>Sin alarmas configuradas</div>
  );

  const counts = alarms.reduce((acc, a) => { acc[a.state] = (acc[a.state] || 0) + 1; return acc; }, {});

  return (
    <div>
      {/* Stats strip — replaces the donut */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        {[
          { key:'ALARM',            label:'En alarma',  color:'var(--red)', bg:'var(--red-dim)', border:'var(--red-dim)' },
          { key:'OK',               label:'OK',         color:'var(--green)', bg:'var(--green-dim)', border:'var(--green-dim)' },
          { key:'INSUFFICIENT_DATA',label:'Sin datos',  color:'var(--ink-2)', bg:'var(--bg-subtle)', border:'var(--border)' },
        ].map(({ key, label, color, bg, border }) => (
          <div key={key} style={{ padding:'12px 20px', background:bg, borderRadius:8,
            border:`1px solid ${border}`, minWidth:100 }}>
            <div style={{ fontSize:26, fontWeight:800, color }}>{counts[key] || 0}</div>
            <div style={{ fontSize:11, color:'var(--ink-2)', marginTop:2 }}>{label}</div>
          </div>
        ))}
        <div style={{ padding:'12px 20px', background:'var(--bg-subtle)', borderRadius:8,
          border:'1px solid var(--border)', minWidth:100 }}>
          <div style={{ fontSize:26, fontWeight:800, color:'var(--ink)' }}>{alarms.length}</div>
          <div style={{ fontSize:11, color:'var(--ink-2)', marginTop:2 }}>Total</div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Alarma</th>
                <th style={{ width:100 }}>Estado</th>
                <th style={{ width:180 }}>Métrica</th>
                <th style={{ width:160 }}>Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {alarms.map((a, i) => {
                const st = ALARM_STATE[a.state] || ALARM_STATE.INSUFFICIENT_DATA;
                return (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight:500, fontSize:13 }}>{a.name}</div>
                      {a.description && <div style={{ fontSize:11, color:'var(--ink-3)' }}>{a.description}</div>}
                    </td>
                    <td>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4,
                        background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      {a.namespace ? a.namespace.replace('AWS/','') + ' · ' : ''}{a.metric}
                    </td>
                    <td style={{ fontSize:12, color:'var(--ink-2)' }}>
                      {a.updated_at ? new Date(a.updated_at).toLocaleString('es-AR') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── AccountSelector — reemplaza AWSConfigForm ──────────────────────────────
// Reads accounts from /api/integrations and lets user assign them to monitoring/net
function AWSConfigForm({ onSaved, token, initialConfig }) {
  const [accounts, setAccounts]   = useState([]);
  const [selMain, setSelMain]     = useState(initialConfig?.account_id || '');
  const [selNet, setSelNet]       = useState(initialConfig?.net_account_id || '');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/integrations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAccounts(Array.isArray(d) ? d.filter(a => a.provider === 'aws') : []); })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    if (!selMain) return setError('Seleccioná una cuenta AWS para monitoreo.');
    setSaving(true); setError(null);
    try {
      const body = { account_id: selMain };
      if (selNet) body.net_account_id = selNet;
      const r = await fetch(`${API_URL}/api/monitoring/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const selStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border-2)', borderRadius: 7,
    fontSize: 13, background: 'var(--surface)', color: 'var(--ink)',
  };

  return (
    <div style={{ maxWidth: 520, margin: '40px auto', padding: 32, border: '1px solid var(--border)', borderRadius: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Seleccionar cuentas AWS</div>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>
        Elegí qué cuenta usar para CloudWatch. Podés agregar o editar cuentas en{' '}
        <a href="/integrations" style={{ color: '#f97316', fontWeight: 600 }}>Integraciones</a>.
      </p>

      {error && (
        <div style={{ padding: '8px 12px', background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 6, fontSize: 13, marginBottom: 16, border: '1px solid var(--red-dim)' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '16px 0' }}>Cargando cuentas…</div>
      ) : accounts.length === 0 ? (
        <div style={{ background: 'var(--amber-dim)', border: '1px solid #fde047', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: 'var(--amber)', marginBottom: 20 }}>
          No hay cuentas AWS configuradas.{' '}
          <a href="/integrations" style={{ color: '#f97316', fontWeight: 600 }}>Ir a Integraciones →</a>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>
              Cuenta de Monitoreo (CloudWatch, EC2, Costos) *
            </label>
            <select style={selStyle} value={selMain} onChange={e => setSelMain(e.target.value)}>
              <option value="">— Seleccioná una cuenta —</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} · {a.access_key_hint} · {a.region}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 6 }}>
              Cuenta de Networking (VPN, Transit GW, Direct Connect){' '}
              <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— opcional</span>
            </label>
            <select style={selStyle} value={selNet} onChange={e => setSelNet(e.target.value)}>
              <option value="">— Misma que monitoreo —</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} · {a.access_key_hint} · {a.region}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {accounts.length > 0 && (
        <button className="btn btn-primary" onClick={save} disabled={saving} style={{ width: '100%' }}>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      )}
    </div>
  );
}


// ── MonitoringCloud ───────────────────────────────────────────────────────────
export default function MonitoringCloud() {
  const [provider, setProvider]     = useState('aws');
  const [awsTab, setAwsTab]         = useState('dashboard');
  const [config, setConfig]         = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [resourceCounts, setResourceCounts] = useState(null);
  const [accounts, setAccounts]     = useState([]);   // [{id, name, provider, environment}]
  const [selectedEnv, setSelectedEnv] = useState(''); // '' = Todos, 'PROD'|'TEST'|'DESA'

  const token = localStorage.getItem('token');

  const loadConfig = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/monitoring/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setConfig(d))
      .catch(() => setConfig({ configured: false }))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Load cloud accounts for env selector
  useEffect(() => {
    if (!config?.configured) return;
    fetch(`${API_URL}/api/monitoring/aws/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setAccounts(Array.isArray(d) ? d : []))
      .catch(() => setAccounts([]));
  }, [config, token]);

  // Selected account ID — when env is chosen, use the first account matching that env
  const selectedAccountId = selectedEnv
    ? (accounts.find(a => a.environment === selectedEnv)?.id || null)
    : null;

  useEffect(() => {
    setResourceCounts(null); // reset mientras carga (muestra todos los tabs)
    if (!config?.configured) return;
    const qs = selectedAccountId ? `?accountId=${selectedAccountId}` : '';
    fetch(`${API_URL}/api/monitoring/aws/resource-counts${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setResourceCounts(typeof d === 'object' && d !== null && !Array.isArray(d) ? d : null))
      .catch(() => setResourceCounts(null)); // en error: mantener todos los tabs visibles
  }, [config, token, selectedAccountId]);

  // Environments that have at least one account
  const availableEnvs = [...new Set(accounts.map(a => a.environment).filter(Boolean))].sort();

  const visibleServices = AWS_SERVICES.filter(s => {
    if (!s.namespace) return true;
    if (resourceCounts === null) return true;
    return (resourceCounts[s.namespace] || 0) > 0;
  });

  const activeService = AWS_SERVICES.find(s => s.id === awsTab);

  const tabStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: active ? 'var(--amber-dim)' : 'none',
    border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: active ? 600 : 400,
    color: active ? 'var(--amber)' : 'var(--ink-2)',
    borderBottom: `2px solid ${active ? '#f97316' : 'transparent'}`,
    marginBottom: -2, whiteSpace: 'nowrap',
    transition: 'color 0.15s, background 0.15s',
  });

  const providerTabStyle = (active) => ({
    padding: '10px 18px', background:'none', border:'none', cursor:'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    color: active ? 'var(--accent)' : 'var(--ink-2)',
    borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
    marginBottom: -2,
  });

  return (
    <div style={{ padding:'24px 32px', maxWidth:1280, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700 }}>Monitoreo Cloud</h1>
          <p style={{ margin:'4px 0 0', color:'var(--ink-2)', fontSize:14 }}>
            Métricas en tiempo real desde proveedores de nube
          </p>
        </div>
        {provider === 'aws' && (
          <button className="btn btn-secondary" onClick={() => setShowConfig(v => !v)}
            style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <Settings size={14} />
            {showConfig ? 'Cerrar config' : (config?.configured ? 'AWS configurado' : 'Configurar AWS')}
            {config?.net_configured && (
              <span style={{ fontSize:10, background:'var(--green-dim)', color:'var(--green)',
                border:'1px solid var(--green-dim)', borderRadius:4, padding:'1px 6px', fontWeight:600 }}>
                NET
              </span>
            )}
          </button>
        )}
      </div>

      {/* Provider tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid var(--border)', marginBottom:28, gap:2 }}>
        {PROVIDERS.map(p => (
          <button key={p.id} onClick={() => setProvider(p.id)} style={providerTabStyle(provider === p.id)}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Non-AWS placeholder */}
      {provider !== 'aws' && (
        <div style={{ textAlign:'center', padding:'60px 0', color:'var(--ink-3)' }}>
          <Cloud size={40} style={{ opacity:.3, display:'block', margin:'0 auto 12px' }} />
          <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Próximamente</div>
          <div style={{ fontSize:14 }}>
            Integración con {PROVIDERS.find(p => p.id === provider)?.label} en desarrollo.
          </div>
        </div>
      )}

      {/* AWS */}
      {provider === 'aws' && (
        <>
          {loading && <div style={{ textAlign:'center', padding:48, color:'var(--ink-3)', fontSize:13 }}>Cargando…</div>}

          {!loading && showConfig && (
            <AWSConfigForm token={token} initialConfig={config}
              onSaved={() => { setShowConfig(false); loadConfig(); }} />
          )}

          {!loading && !config?.configured && !showConfig && (
            <div style={{ textAlign:'center', padding:'48px 0' }}>
              <Cloud size={40} style={{ color:'var(--border-2)', display:'block', margin:'0 auto 12px' }} />
              <p style={{ fontSize:15, color:'var(--ink-2)', marginBottom:12 }}>No hay una cuenta AWS configurada.</p>
              <button className="btn btn-primary" onClick={() => setShowConfig(true)} style={{ fontSize:13 }}>
                <Settings size={14} style={{ marginRight:6 }} />
                Configurar cuenta AWS
              </button>
            </div>
          )}

          {!loading && config?.configured && !showConfig && (
            <>
              {/* AWS service tabs */}
              {/* ── Environment selector ─────────────────────────────── */}
              {availableEnvs.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16,
                  padding:'8px 14px', background:'var(--bg-subtle)', border:'1px solid var(--border)',
                  borderRadius:8 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', marginRight:4 }}>Ambiente:</span>
                  {[{v:'', label:'Todos', bg:'var(--bg)', color:'var(--ink-2)', border:'var(--border)'},
                    {v:'PROD',       label:'PROD',       bg:'var(--green-dim)', color:'var(--green)', border:'var(--green-dim)'},
                    {v:'TEST',       label:'TEST',       bg:'var(--accent-dim)', color:'var(--accent)', border:'var(--accent-dim)'},
                    {v:'DESA',       label:'DESA',       bg:'var(--amber-dim)', color:'var(--amber)', border:'var(--amber-dim)'},
                    {v:'NETWORKING', label:'Networking', bg:'var(--bg-subtle)', color:'#7e22ce', border:'#e9d5ff'},
                  ].filter(e => e.v === '' || availableEnvs.includes(e.v)).map(e => (
                    <button key={e.v} onClick={() => setSelectedEnv(e.v)}
                      style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:5, cursor:'pointer',
                        border: selectedEnv === e.v ? `2px solid ${e.color}` : `1px solid ${e.border}`,
                        background: selectedEnv === e.v ? e.bg : '#fff',
                        color: selectedEnv === e.v ? e.color : 'var(--ink-2)' }}>
                      {e.label}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Service tabs ─────────────────────────────────────── */}
              <div style={{ display:'flex', flexWrap:'nowrap', overflowX:'auto', gap:0,
                borderBottom:'1px solid var(--border)', marginBottom:24 }}>
                {visibleServices.map(s => {
                  const Icon = s.icon;
                  const active = awsTab === s.id;
                  return (
                    <button key={s.id} onClick={() => setAwsTab(s.id)} style={tabStyle(active)}>
                      {Icon && <Icon size={13} style={{ color: active ? '#f97316' : 'var(--ink-3)', flexShrink: 0 }} />}
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {awsTab === 'dashboard' && <NocDashboard token={token} accountId={selectedAccountId} />}
              {awsTab === 'alarms'   && <AlarmsPanel  token={token} accountId={selectedAccountId} />}
              {awsTab === 'alerts'   && <AlertsPanel  token={token} />}
              {awsTab === 'logs'     && <LogsPanel    token={token} accountId={selectedAccountId} />}
              {!['dashboard','alarms','alerts','logs'].includes(awsTab) && activeService?.namespace && (
                <ServicePanel namespace={activeService.namespace} token={token} accountId={selectedAccountId} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
